import React, { useEffect, useState } from 'react';
import { Result, Button, Card, Spin, message, Progress, Row, Col, Typography, Space, Divider } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, ClockCircleOutlined, CloudServerOutlined, CheckCircleFilled } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';

const { Title, Text } = Typography;

const PaymentNotify: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState<'success' | 'pending' | 'failed'>('pending');
  const [orderInfo, setOrderInfo] = useState<any>(null);
  const [installProgress, setInstallProgress] = useState<any>(null);
  const [serverId, setServerId] = useState<number | null>(null);
  const [showProgress, setShowProgress] = useState(false);

  // 触发服务器创建流程
  const triggerServerCreation = async (orderId: string) => {
    try {
      console.log('🚀 开始触发服务器创建流程，订单ID:', orderId);
      
      // 调用后端API触发服务器创建
      const response = await api.post('/payment/rainbow/trigger-server-creation', {
        orderId: orderId
      });
      
      if (response.data.success) {
        console.log('✅ 服务器创建流程已触发，服务器ID:', response.data.serverId);
        setServerId(response.data.serverId);
      } else if (response.data.message === '服务器正在创建中，请稍候...') {
        console.log('⏳ 服务器正在创建中，开始轮询检查状态');
        // 开始轮询检查订单状态
        pollOrderStatus(orderId);
      } else {
        console.error('❌ 触发服务器创建失败:', response.data.message);
      }
    } catch (error) {
      console.error('❌ 触发服务器创建异常:', error);
      // 如果API调用失败，也尝试轮询检查状态
      console.log('⏳ API调用失败，开始轮询检查状态');
      pollOrderStatus(orderId);
    }
  };

  // 轮询检查订单状态
  const pollOrderStatus = async (orderId: string) => {
    let attempts = 0;
    const maxAttempts = 20; // 最多轮询20次，每次3秒，总共1分钟
    
    const poll = async () => {
      try {
        attempts++;
        console.log(`🔄 轮询检查订单状态 (${attempts}/${maxAttempts}):`, orderId);
        
        const response = await api.get(`/payment/rainbow/order-status/${orderId}`);
        const order = response.data;
        
        if (order.server_id) {
          console.log('✅ 服务器创建完成，服务器ID:', order.server_id);
          setServerId(order.server_id);
          
          // 显示完成状态
          setInstallProgress({
            step: 5,
            message: '服务器创建完成！',
            serverId: order.server_id
          });
          
          setTimeout(() => {
            message.success('服务器创建完成！');
            navigate('/servers');
          }, 2000);
          
          return; // 停止轮询
        }
        
        if (attempts < maxAttempts) {
          setTimeout(poll, 3000); // 3秒后再次检查
        } else {
          console.error('❌ 轮询超时，服务器创建可能失败');
        }
      } catch (error) {
        console.error('❌ 轮询检查订单状态失败:', error);
        if (attempts < maxAttempts) {
          setTimeout(poll, 3000);
        }
      }
    };
    
    poll();
  };

  useEffect(() => {
    handlePaymentNotify();
  }, []);

  // 监听Socket.IO的服务器安装进度事件
  useEffect(() => {
    console.log('🔌 Socket.IO监听检查:', {
      hasIO: !!window.io,
      serverId: serverId,
      showProgress: showProgress
    });
    
    if (window.io && showProgress) {
      console.log('🔌 监听服务器安装进度，显示进度卡片');
      
      // 监听服务器安装进度
      window.io.on('server-install-progress', (data: any) => {
        console.log('📊 收到服务器安装进度:', data);
        
        // 如果还没有serverId，或者serverId匹配，都显示进度
        if (!serverId || data.serverId === serverId) {
          console.log('✅ 更新安装进度');
          setInstallProgress(data);
          
          // 如果安装完成，显示完成状态并跳转
          if (data.step === 5) {
            setTimeout(() => {
              message.success('服务器创建完成！');
              navigate('/servers');
            }, 15000);
          }
        } else {
          console.log('❌ 服务器ID不匹配:', {
            received: data.serverId,
            expected: serverId
          });
        }
      });

      // 清理监听器
      return () => {
        if (window.io) {
          window.io.off('server-install-progress');
        }
      };
    }
  }, [showProgress, serverId, navigate]);

  const handlePaymentNotify = async () => {
    try {
      const orderId = searchParams.get('out_trade_no');
      const tradeNo = searchParams.get('trade_no');
      const tradeStatus = searchParams.get('trade_status');
      const money = searchParams.get('money');

      if (!orderId) {
        setPaymentStatus('failed');
        setLoading(false);
        return;
      }

      console.log('支付通知参数:', {
        orderId,
        tradeNo,
        tradeStatus,
        money
      });

      // 查询订单状态
      const response = await api.get(`/payment/rainbow/order-status/${orderId}`);
      const order = response.data;

      console.log('📋 订单状态查询结果:', order);
      console.log('📋 订单详情:', {
        orderId: order.orderId,
        status: order.status,
        type: order.type,
        server_id: order.server_id,
        amount: order.amount
      });
      setOrderInfo(order);

      if (order.status === 'completed') {
        setPaymentStatus('success');
        message.success('支付成功！');
        
        // 如果是服务器购买，检查是否有server_id
        if (order.type === 'server_purchase') {
          if (order.server_id) {
            // 服务器已创建，直接跳转到服务器列表
            console.log('🎮 服务器购买成功，服务器已创建，服务器ID:', order.server_id);
            setTimeout(() => {
              message.success('服务器创建完成！');
              navigate('/servers');
            }, 1000);
          } else {
            // 服务器还未创建，显示进度卡片并触发服务器创建流程
            console.log('🚀 服务器购买成功，但服务器还未创建，触发创建流程');
            setShowProgress(true);
            await triggerServerCreation(orderId);
          }
        }
      } else if (order.status === 'pending') {
        setPaymentStatus('pending');
        // 继续轮询检查状态
        setTimeout(handlePaymentNotify, 3000);
      } else {
        setPaymentStatus('failed');
      }
    } catch (error) {
      console.error('处理支付通知失败:', error);
      setPaymentStatus('failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '24px'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {loading && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '400px' 
          }}>
            <Card style={{ textAlign: 'center', padding: '40px' }}>
              <Spin 
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} 
                tip="正在处理支付通知..."
              />
            </Card>
          </div>
        )}

        {!loading && paymentStatus === 'success' && (
          <Row gutter={[24, 24]}>
            {/* 支付信息卡片 */}
            <Col xs={24} lg={12}>
              <Card 
                style={{ 
                  height: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                  border: 'none'
                }}
                bodyStyle={{ padding: '32px' }}
              >
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                  <CheckCircleFilled style={{ fontSize: '48px', color: '#52c41a', marginBottom: '16px' }} />
                  <Title level={2} style={{ margin: 0, color: '#52c41a' }}>支付成功！</Title>
                  <Text type="secondary" style={{ fontSize: '16px' }}>您的订单已支付成功</Text>
                </div>
                
                {orderInfo && (
                  <div style={{ 
                    background: '#f8f9fa', 
                    borderRadius: '12px', 
                    padding: '20px',
                    marginBottom: '24px'
                  }}>
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text strong>订单号：</Text>
                        <Text code>{orderInfo.orderId}</Text>
                      </div>
                      <Divider style={{ margin: '12px 0' }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text strong>支付金额：</Text>
                        <Text style={{ color: '#52c41a', fontSize: '18px', fontWeight: 'bold' }}>
                          ¥{orderInfo.amount}
                        </Text>
                      </div>
                      <Divider style={{ margin: '12px 0' }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text strong>订单类型：</Text>
                        <Text>{orderInfo.type === 'balance_recharge' ? '余额充值' : '服务器购买'}</Text>
                      </div>
                      <Divider style={{ margin: '12px 0' }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text strong>支付时间：</Text>
                        <Text>{new Date(orderInfo.completedAt).toLocaleString()}</Text>
                      </div>
                    </Space>
                  </div>
                )}

                <Space style={{ width: '100%', justifyContent: 'center' }}>
                  <Button type="primary" size="large" onClick={() => navigate('/dashboard')}>
                    返回控制台
                  </Button>
                  <Button size="large" onClick={() => navigate('/servers')}>
                    查看服务器
                  </Button>
                </Space>
              </Card>
            </Col>

            {/* 服务器创建进度卡片 */}
            <Col xs={24} lg={12}>
              <Card 
                style={{ 
                  height: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                  border: 'none'
                }}
                bodyStyle={{ padding: '32px' }}
              >
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                  <CloudServerOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '16px' }} />
                  <Title level={3} style={{ margin: 0 }}>服务器创建进度</Title>
                  <Text type="secondary">正在为您创建游戏服务器</Text>
                </div>

                {showProgress && (
                  <div style={{ textAlign: 'center' }}>
                    <Progress
                      type="circle"
                      percent={installProgress ? Math.round((installProgress.step / 5) * 100) : 0}
                      format={() => installProgress ? `${installProgress.step}/5` : '0/5'}
                      strokeColor={{
                        '0%': '#108ee9',
                        '100%': '#87d068',
                      }}
                      size={120}
                      style={{ marginBottom: '24px' }}
                    />
                    
                    <div style={{ 
                      background: '#f8f9fa', 
                      borderRadius: '12px', 
                      padding: '20px',
                      marginBottom: '24px'
                    }}>
                      <Title level={4} style={{ margin: '0 0 16px 0' }}>
                        {installProgress ? installProgress.message : '正在准备创建服务器...'}
                      </Title>
                      
                      {installProgress && (
                        <div style={{ textAlign: 'left' }}>
                          {installProgress.step === 1 && <Text>📁 正在创建服务器目录结构...</Text>}
                          {installProgress.step === 2 && <Text>📋 正在复制配置文件...</Text>}
                          {installProgress.step === 3 && <Text>⚙️ 正在更新服务器设置...</Text>}
                          {installProgress.step === 4 && <Text>🚀 正在创建启动脚本...</Text>}
                          {installProgress.step === 5 && <Text>✅ 服务器创建完成！</Text>}
                        </div>
                      )}
                      
                      {!installProgress && (
                        <Text>请稍候，我们正在为您创建游戏服务器</Text>
                      )}
                    </div>

                    {installProgress && installProgress.step === 5 && (
                      <div style={{ 
                        background: '#f6ffed', 
                        border: '1px solid #b7eb8f',
                        borderRadius: '12px', 
                        padding: '16px',
                        marginBottom: '16px'
                      }}>
                        <CheckCircleFilled style={{ color: '#52c41a', marginRight: '8px' }} />
                        <Text style={{ color: '#52c41a' }}>服务器创建完成，即将跳转到服务器列表</Text>
                      </div>
                    )}
                  </div>
                )}

                {!showProgress && (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '40px 20px',
                    color: '#999'
                  }}>
                    <ClockCircleOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                    <Text>等待服务器创建开始...</Text>
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        )}

        {!loading && paymentStatus === 'pending' && (
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <Card style={{ 
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              border: 'none',
              textAlign: 'center',
              padding: '40px'
            }}>
              <LoadingOutlined style={{ fontSize: 64, color: '#1890ff', marginBottom: '24px' }} />
              <Title level={2}>支付处理中...</Title>
              <Text type="secondary" style={{ fontSize: '16px', display: 'block', marginBottom: '32px' }}>
                我们正在确认您的支付，请稍候。
              </Text>
              <Space>
                <Button type="primary" size="large" onClick={handlePaymentNotify}>
                  刷新状态
                </Button>
                <Button size="large" onClick={() => navigate('/dashboard')}>
                  返回控制台
                </Button>
              </Space>
            </Card>
          </div>
        )}

        {!loading && paymentStatus === 'failed' && (
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <Card style={{ 
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              border: 'none',
              textAlign: 'center',
              padding: '40px'
            }}>
              <CloseCircleOutlined style={{ fontSize: 64, color: '#ff4d4f', marginBottom: '24px' }} />
              <Title level={2} style={{ color: '#ff4d4f' }}>支付失败</Title>
              <Text type="secondary" style={{ fontSize: '16px', display: 'block', marginBottom: '32px' }}>
                支付过程中出现问题，请重试或联系客服。
              </Text>
              <Space>
                <Button type="primary" size="large" onClick={() => navigate('/servers/create')}>
                  重新购买
                </Button>
                <Button size="large" onClick={() => navigate('/dashboard')}>
                  返回控制台
                </Button>
              </Space>
            </Card>
          </div>
        )}

      </div>
    </div>
  );
};

export default PaymentNotify;